/**
 * Tour progress reporting -- wired to the server-side TourProgress API
 * (EPIC #1321; engine seam from 1/9, persistence from 2/9 + 3/9).
 *
 * The engine calls ``recordTourProgress`` when a tour ends. We translate the
 * engine outcome into the persisted TourProgress status and upsert it so the
 * tour's state survives across devices/sessions and a resumable (abandoned)
 * tour can be re-offered from Training (7/9):
 *
 *   completed  -> "completed"   (the user finished the whole tour)
 *   skipped    -> "dismissed"   (the user chose to stop -- treated as done)
 *   cancelled  -> "in_progress" (X / Esc / overlay -- RESUMABLE, keeps the step)
 *
 * The call is fire-and-forget so the engine's synchronous ``void`` signature is
 * unchanged: a failed write never blocks the UI or throws into the engine.
 *
 * REQs: D4-ONB-005 (persistence), D4-ONB-007 (resumable welcome tours).
 */
import { upsertTourProgress } from "../../api/tours";
import type { TourStatus } from "../../api/tours";

export type TourOutcome = "completed" | "skipped" | "cancelled";

export interface TourProgressEvent {
  tourId: string;
  outcome: TourOutcome;
  /** Index of the step the user was on when the tour ended (0-based). */
  lastStepIndex: number;
  /** Number of visible steps in the tour for this user. */
  totalSteps: number;
}

/** Map an engine outcome to the persisted server-side status. */
export function statusForOutcome(outcome: TourOutcome): TourStatus {
  switch (outcome) {
    case "completed":
      return "completed";
    case "skipped":
      return "dismissed";
    case "cancelled":
    default:
      // X / Esc mid-tour: keep it RESUMABLE rather than marking it done, so it
      // re-surfaces from Training at the step the user left off on.
      return "in_progress";
  }
}

export function recordTourProgress(event: TourProgressEvent): void {
  if (import.meta.env.DEV) {
    console.debug("[tour] progress", event);
  }
  const status = statusForOutcome(event.outcome);
  // Fire-and-forget: never block the UI; a failed write is non-fatal.
  void upsertTourProgress({
    tour_id: event.tourId,
    status,
    current_step: event.lastStepIndex,
  }).catch((err) => {
    if (import.meta.env.DEV) {
      console.debug("[tour] progress upsert failed", err);
    }
  });
}

/**
 * Mark a tour STARTED (status "in_progress", step 0) the moment it begins, so a
 * tour the user opens but never finishes is already recorded as resumable even
 * before the first advance. Fire-and-forget; safe to call repeatedly (idempotent
 * upsert keyed on the user + tour).
 */
export function recordTourStarted(tourId: string): void {
  void upsertTourProgress({
    tour_id: tourId,
    status: "in_progress",
    current_step: 0,
  }).catch((err) => {
    if (import.meta.env.DEV) {
      console.debug("[tour] start upsert failed", err);
    }
  });
}
